import React from 'react';
import { motion } from 'framer-motion';
import { useReveal, useBentoGlow } from '../hooks/useScrollReveal';
import '../styles/Projects.css';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

const Projects = () => {
    const [ref, isVisible] = useReveal();
    const glowRef = useBentoGlow();

    const projects = [
        {
            id: 'feat', title: 'CivitasFix', type: 'Full Stack',
            description: 'Campus community platform with real-time collaboration and socket integration.',
            image: '/civitasfix.webp',
            technologies: ['React', 'Node.js', 'Socket.io'],
            links: [{ name: 'GitHub ↗', url: 'https://github.com/nandanannn24/civitasfix.git' }]
        },
        {
            id: 'p1', title: 'EcoTrack', type: 'Frontend',
            description: 'Environmental analytics dashboard.',
            image: '/ecotrack.webp',
            technologies: ['React', 'Firebase'],
            links: [{ name: 'GitHub ↗', url: 'https://github.com/nandanannn24/EcoTrack' }]
        },
        {
            id: 'p2', title: 'Papat Community', type: 'Frontend',
            description: 'Business networking.',
            image: '/papatcommunity.webp',
            technologies: ['React', 'Vercel'],
            links: [{ name: 'Live ↗', url: 'https://comunitypapat.vercel.app/' }]
        },
        {
            id: 'p3', title: 'Kawan UMKM', type: 'Full Stack',
            description: 'Business solutions for small enterprises.',
            image: '/kawanumkm.webp',
            technologies: ['React', 'Netlify'],
            links: [
                { name: 'FE Repo ↗', url: 'https://github.com/nandanannn24/kawan-umkm-frontend.git' },
                { name: 'BE Repo ↗', url: 'https://github.com/nandanannn24/kawan-umkm-backend.git' }
            ]
        },
        {
            id: 'p4', title: 'Lockerroom Clothing', type: 'Frontend',
            description: 'Clothing line & screen printing (sablon) website.',
            image: '/lr.webp',
            technologies: ['React', 'Netlify'],
            links: [{ name: 'Live ↗', url: 'https://lockerroom-clothing.netlify.app' }]
        },
        {
            id: 'p5', title: 'Teras Rumah Karlos', type: 'Frontend',
            description: 'Local ATK, Printing, and Photo business platform.',
            image: '/teras.webp',
            technologies: ['React', 'Netlify'],
            links: [{ name: 'Live ↗', url: 'https://teras-rumah-karlos.netlify.app' }]
        },
        {
            id: 'p6', title: 'Undercover', type: 'Other Project',
            description: 'Development & Exploration Project.',
            image: '/undercover.webp',
            technologies: ['GitHub'],
            links: [{ name: 'GitHub ↗', url: 'https://github.com/nandanannn24/undercover.git' }]
        },
        {
            id: 'p7', title: 'Minecraft Tunnel', type: 'Other Project',
            description: 'Tunneling network solution for Minecraft server.',
            image: '/mc.webp',
            technologies: ['Network', 'Server'],
            links: [{ name: 'GitHub ↗', url: 'https://github.com/nandanannn24/minecraft-tunnel.git' }]
        }
    ];

    return (
        <section id="projects" className="projects" ref={ref}>
            <div className="container" ref={glowRef}>
                {isVisible && (
                    <motion.div
                        className="projects-grid"
                        initial="hidden"
                        animate="visible"
                        variants={{ visible: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } } }}
                    >
                        {/* Header Cell */}
                        <motion.div className="bento-card section-header-card project-header" variants={fadeUp}>
                            <span className="section-label">Work</span>
                            <h2 className="section-heading">
                                Featured <span className="gradient-text">Projects</span>
                            </h2>
                            <p className="section-desc">
                                A selection of projects I've built and contributed to.
                            </p>
                        </motion.div>

                        {/* Standard Projects */}
                        {projects.map((p) => (
                            <motion.div
                                key={p.id}
                                className={`bento-card typo-project-card ${p.id}`}
                                variants={fadeUp}
                                transition={{ duration: 0.5 }}
                                whileHover="hover"
                            >
                                <motion.div className="typo-project-bg">
                                    <img src={p.image} alt={p.title} loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
                                </motion.div>
                                <div className="typo-project-body">
                                    <div className="typo-project-top">
                                        <span className="typo-project-type">{p.type}</span>
                                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                            {p.links && p.links.map((link, idx) => (
                                                <motion.a
                                                    key={idx}
                                                    href={link.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="project-demo-link"
                                                    variants={{ hover: { opacity: 1, y: 0 }, initial: { opacity: 0, y: -5 } }}
                                                >
                                                    {link.name}
                                                </motion.a>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="typo-project-bottom">
                                        <h3 className="massive-project-title">{p.title}</h3>
                                        <p className="typo-project-desc">{p.description}</p>
                                        <div className="typo-tags-container">
                                            {p.technologies.map((t, i) => <span key={i} className="typo-tag">{t}</span>)}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}

                        {/* Archives V1 */}
                        <motion.a
                            href="https://sinanda.netlify.app/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bento-card typo-project-card v1"
                            variants={fadeUp}
                            whileHover={{ y: -5 }}
                        >
                            <motion.div className="typo-project-bg">
                                <img src="/porto1.webp" alt="Portfolio 1" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
                            </motion.div>
                            <div className="typo-project-body" style={{ justifyContent: 'flex-end' }}>
                                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, transparent 60%)', zIndex: -1, borderRadius: 'inherit' }} />
                                <span className="typo-project-type">Archive</span>
                                <h3 className="massive-project-title" style={{ fontSize: '1.2rem', marginBottom: 0 }}>Version 1 ↗</h3>
                            </div>
                        </motion.a>

                        {/* Archives V2 */}
                        <motion.a
                            href="https://nandanannn.netlify.app/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bento-card typo-project-card v2"
                            variants={fadeUp}
                            whileHover={{ y: -5 }}
                        >
                            <motion.div className="typo-project-bg">
                                <img src="/porto2.webp" alt="Portfolio 2" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
                            </motion.div>
                            <div className="typo-project-body" style={{ justifyContent: 'flex-end' }}>
                                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, transparent 60%)', zIndex: -1, borderRadius: 'inherit' }} />
                                <span className="typo-project-type">Archive</span>
                                <h3 className="massive-project-title" style={{ fontSize: '1.2rem', marginBottom: 0 }}>Version 2 ↗</h3>
                            </div>
                        </motion.a>

                    </motion.div>
                )}
            </div>
        </section>
    );
};

export default Projects;